/**
 * Boîte à outils — repère d'une figure SVG (pur, sans DOM sauf `pointerToViewBox`).
 *
 * Une figure carrée de `size` px (viewBox) montre la fenêtre `vp` du plan ;
 * `tx`/`ty` passent du plan à l'écran (y vers le haut → vers le bas), `ix`/`iy`
 * font le chemin inverse pour les points qu'on déplace au doigt.
 */

export interface Viewport {
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
}

export interface Frame {
  size: number;
  vp: Viewport;
  tx: (x: number) => number;
  ty: (y: number) => number;
  ix: (px: number) => number;
  iy: (py: number) => number;
  /** px par unité, en x (= en y si la fenêtre est carrée) */
  unit: number;
}

export function makeFrame(vp: Viewport, size: number): Frame {
  const kx = size / Math.max(vp.xMax - vp.xMin, 1e-9);
  const ky = size / Math.max(vp.yMax - vp.yMin, 1e-9);
  return {
    size,
    vp,
    tx: (x) => (x - vp.xMin) * kx,
    ty: (y) => (vp.yMax - y) * ky,
    ix: (px) => vp.xMin + px / kx,
    iy: (py) => vp.yMax - py / ky,
    unit: kx,
  };
}

/** Position d'un pointeur en coordonnées du viewBox (figure carrée de `size` px). */
export function pointerToViewBox(
  svg: SVGSVGElement,
  clientX: number,
  clientY: number,
  size: number,
): { px: number; py: number } | null {
  const r = svg.getBoundingClientRect();
  if (!r.width || !r.height) return null;
  return {
    px: ((clientX - r.left) / r.width) * size,
    py: ((clientY - r.top) / r.height) * size,
  };
}

/** Arrondi au pas `step` (0,5 → demi-unités), sans « −0 ». */
export function snap(v: number, step: number): number {
  const r = Math.round(v / step) * step;
  const clean = Number(r.toFixed(10));
  return Object.is(clean, -0) ? 0 : clean;
}

export function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

export interface Pt {
  x: number;
  y: number;
}

/** Point du plan ramené sur la grille `step`, sans sortir de la fenêtre (marge `margin`). */
export function snapPoint(p: Pt, vp: Viewport, step: number, margin = 0): Pt {
  const lo = (min: number) => snap(Math.ceil((min + margin) / step - 1e-9) * step, step);
  const hi = (max: number) => snap(Math.floor((max - margin) / step + 1e-9) * step, step);
  return {
    x: clamp(snap(p.x, step), lo(vp.xMin), hi(vp.xMax)),
    y: clamp(snap(p.y, step), lo(vp.yMin), hi(vp.yMax)),
  };
}
